/**
 * Zustand store for the /review overlay.
 *
 * Loads the review queue (tasks with unread agent notifications + the
 * recently-reviewed tail) and tracks which frame the user is currently
 * inspecting. When a tick is activated, the owning task's unread
 * notifications are marked read fire-and-forget so only navigated-to
 * tasks clear — Esc without navigation leaves them unread.
 */

import { create } from "zustand";
import { mirroredStore } from "@/lib/mirrored-store";
import { getReviewQueue, type ReviewQueueTask } from "@/api/tasks";
import { markRead } from "@/api/notifications";
import { useProjectStore } from "@/stores/project-store";
import {
  useDesktopMode,
  type DesktopMode,
  type PeekReason,
} from "@/stores/desktop-mode-store";
import { useNotificationStore } from "@/stores/notification-store";
import { useFramesStore } from "@/stores/frames-store";
import { isWallActive } from "@/stores/workspace-view-store";
import { captureDesktopChatAuthScopeGuard } from "@/desktop/desktop-chat-auth-scope";
import { translate } from "@/i18n";
import { storesNoticesMessages } from "@/i18n/messages/stores-notices";

interface ReviewDesktopModeSnapshot {
  mode: DesktopMode;
  denkWorkspacePreview: boolean;
  denkCanvasCapture: boolean;
  peekReason: PeekReason | null;
}

interface ReviewModeState {
  isOpen: boolean;
  loading: boolean;
  error: string | null;
  unread: ReviewQueueTask[];
  recent: ReviewQueueTask[];
  /** Currently active frame, null when task has no frames. */
  activeFrameId: string | null;
  /** Task whose details render on the right, kept in sync with activeFrameId. */
  activeTaskId: string | null;
  /** Task ids whose notifications were marked read during this session. */
  readInSession: Set<string>;
  desktopModeSnapshot: ReviewDesktopModeSnapshot | null;
  openRequestId: number;
}

interface ReviewModeActions {
  openReviewMode: () => Promise<void>;
  closeReviewMode: (options?: { restoreDesktopMode?: boolean }) => void;
  setActiveTask: (taskId: string, frameId: string | null) => void;
  clearForAuthChange: () => void;
}

export type ReviewModeStore = ReviewModeState & ReviewModeActions;

const REVIEW_CAUGHT_UP_TOAST_ID = "review-caught-up";

function pickInitialActive(
  unread: ReviewQueueTask[],
  recent: ReviewQueueTask[],
): { taskId: string | null; frameId: string | null } {
  const first = unread[0] ?? recent[0];
  if (!first) return { taskId: null, frameId: null };
  return { taskId: first.task_id, frameId: first.frame_ids[0] ?? null };
}

function snapshotDesktopMode(): ReviewDesktopModeSnapshot {
  const { mode, denkWorkspacePreview, denkCanvasCapture, peekReason } =
    useDesktopMode.getState();
  return {
    mode,
    denkWorkspacePreview,
    denkCanvasCapture,
    peekReason,
  };
}

function restoreDesktopMode(snapshot: ReviewDesktopModeSnapshot | null) {
  if (!snapshot) return;
  const desktopMode = useDesktopMode.getState();
  if (snapshot.mode === "denk") {
    if (snapshot.denkCanvasCapture) {
      desktopMode.openWorkspace();
      desktopMode.setDenkCanvasCapture(true);
      return;
    }
    desktopMode.openDenkMode();
    if (snapshot.denkWorkspacePreview) {
      useDesktopMode.getState().beginDenkWorkspacePreview();
    }
    return;
  }
  desktopMode.closeWorkspace();
  if (snapshot.mode === "peek" && snapshot.peekReason) {
    useDesktopMode.getState().openPeek(snapshot.peekReason);
  }
}

function waitForReviewLayoutCommit(): Promise<void> {
  return new Promise((resolve) => {
    if (typeof requestAnimationFrame !== "function") {
      setTimeout(resolve, 0);
      return;
    }
    requestAnimationFrame(() => resolve());
  });
}

/**
 * Present the queue's frames in review presentation mode at once, with the
 * loaded ones. The wall holds only the pages it loaded: the others are
 * fetched by id and join the presentation when they land, keeping the
 * frame on show. Lazy imports — presentation-mode lazily imports this store
 * for its mirror-close, so a static import here would be an ESM cycle.
 */
async function presentReviewQueue(
  frameIds: string[],
  {
    stillOpen,
    activeFrameId,
  }: { stillOpen: () => boolean; activeFrameId: () => string | null },
): Promise<void> {
  const [
    { enterPresentationMode },
    { loadUnpagedFrames },
    { usePresentationStore },
  ] = await Promise.all([
    import("@/lib/presentation-mode"),
    import("@/lib/frame-ingest"),
    import("@/stores/presentation-store"),
  ]);
  await waitForReviewLayoutCommit();
  if (!stillOpen()) return;
  const isLoaded = (id: string) => !!useFramesStore.getState().framesById[id];
  const present = (startFrameId: string | null) =>
    enterPresentationMode("review", frameIds, startFrameId ?? undefined);
  if (frameIds.some(isLoaded)) present(activeFrameId());
  const missing = isWallActive() ? frameIds.filter((id) => !isLoaded(id)) : [];
  if (missing.length === 0) return;
  void loadUnpagedFrames(missing).then(({ current }) => {
    if (!current || !stillOpen() || !missing.some(isLoaded)) return;
    const shown = usePresentationStore.getState().presentation;
    present(
      shown?.variant === "review"
        ? (shown.frameIds[shown.activeIndex] ?? null)
        : activeFrameId(),
    );
  });
}

export const useReviewModeStore = create<ReviewModeStore>()(
  mirroredStore("reviewMode", (set, get) => ({
    isOpen: false,
    loading: false,
    error: null,
    unread: [],
    recent: [],
    activeFrameId: null,
    activeTaskId: null,
    readInSession: new Set(),
    desktopModeSnapshot: null,
    openRequestId: 0,

    openReviewMode: async () => {
      const isCurrentAuthScope = captureDesktopChatAuthScopeGuard();
      const desktopModeSnapshot =
        get().desktopModeSnapshot ?? snapshotDesktopMode();
      const openRequestId = get().openRequestId + 1;
      const projectId = useProjectStore.getState().currentProject?.id;
      if (!projectId) {
        set({
          error: translate(storesNoticesMessages, "noActiveProject"),
          isOpen: true,
          loading: false,
          desktopModeSnapshot,
          openRequestId,
        });
        return;
      }
      set({
        isOpen: false,
        loading: true,
        error: null,
        unread: [],
        recent: [],
        activeFrameId: null,
        activeTaskId: null,
        readInSession: new Set(),
        desktopModeSnapshot,
        openRequestId,
      });
      try {
        const data = await getReviewQueue(projectId);
        if (!isCurrentAuthScope()) return;
        if (get().openRequestId !== openRequestId || !get().loading) return;
        if (data.unread.length === 0 && data.recent.length === 0) {
          set({
            isOpen: false,
            loading: false,
            error: null,
            unread: [],
            recent: [],
            activeTaskId: null,
            activeFrameId: null,
            readInSession: new Set(),
            desktopModeSnapshot: null,
          });
          useNotificationStore.getState().addSystemToast({
            id: REVIEW_CAUGHT_UP_TOAST_ID,
            level: "info",
            title: translate(storesNoticesMessages, "caughtUpTitle"),
            description: translate(
              storesNoticesMessages,
              "caughtUpDescription",
            ),
          });
          return;
        }
        useDesktopMode.getState().openWorkspace();
        useDesktopMode.getState().setDenkCanvasCapture(true);
        const initial = pickInitialActive(data.unread, data.recent);
        set({
          isOpen: true,
          loading: false,
          unread: data.unread,
          recent: data.recent,
          activeTaskId: initial.taskId,
          activeFrameId: initial.frameId,
        });
        const frameIds = [...data.unread, ...data.recent].flatMap(
          (t) => t.frame_ids,
        );
        if (frameIds.length === 0) return;
        await presentReviewQueue(frameIds, {
          stillOpen: () =>
            isCurrentAuthScope() &&
            get().openRequestId === openRequestId &&
            get().isOpen,
          activeFrameId: () => get().activeFrameId,
        });
      } catch (err) {
        if (!isCurrentAuthScope()) return;
        const current = get();
        if (
          current.openRequestId !== openRequestId ||
          (!current.loading && !current.isOpen)
        ) {
          return;
        }
        const message =
          err instanceof Error
            ? err.message
            : translate(storesNoticesMessages, "loadFailed");
        set({ loading: false, error: message });
      }
    },

    closeReviewMode: (options) => {
      const desktopModeSnapshot = get().desktopModeSnapshot;
      set({
        isOpen: false,
        loading: false,
        unread: [],
        recent: [],
        activeFrameId: null,
        activeTaskId: null,
        readInSession: new Set(),
        error: null,
        desktopModeSnapshot: null,
        openRequestId: get().openRequestId + 1,
      });
      // If a review presentation is still active (programmatic close, e.g.
      // denk force-close), exit it. exitPresentationMode owns the reverse
      // mirror-close; its isOpen guard sees the set() above, so this never
      // recurses. Lazy imports avoid an ESM cycle with presentation-mode.
      void Promise.all([
        import("@/lib/presentation-mode"),
        import("@/stores/presentation-store"),
      ]).then(([{ exitPresentationMode }, { usePresentationStore }]) => {
        if (
          usePresentationStore.getState().presentation?.variant === "review"
        ) {
          exitPresentationMode();
        }
      });
      if (options?.restoreDesktopMode !== false) {
        restoreDesktopMode(desktopModeSnapshot);
      }
    },

    setActiveTask: (taskId, frameId) => {
      const state = get();
      set({ activeTaskId: taskId, activeFrameId: frameId });

      if (state.readInSession.has(taskId)) return;

      const next = new Set(state.readInSession);
      next.add(taskId);
      set({ readInSession: next });

      const task = state.unread.find((t) => t.task_id === taskId);
      for (const notifId of task?.unread_notification_ids ?? []) {
        markRead(notifId).catch(() => {
          // Best-effort — failures are surfaced by the notification panel.
        });
      }
    },

    clearForAuthChange: () =>
      set((state) => ({
        isOpen: false,
        loading: false,
        error: null,
        unread: [],
        recent: [],
        activeFrameId: null,
        activeTaskId: null,
        readInSession: new Set(),
        desktopModeSnapshot: null,
        openRequestId: state.openRequestId + 1,
      })),
  })),
);
